/* globals window */
import React, { useState, useEffect } from 'react';
import { List, Card, Icon, Typography, Spin, Button } from 'antd';
import AssignmentTurnedInOutlinedIcon from '@material-ui/icons/AssignmentTurnedInOutlined';
import RemoveCircleOutlineOutlinedIcon from '@material-ui/icons/RemoveCircleOutlineOutlined';
import BlurCircularOutlinedIcon from '@material-ui/icons/BlurCircularOutlined';
import InsertInvitationOutlinedIcon from '@material-ui/icons/InsertInvitationOutlined';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import ScheduleIcon from '@material-ui/icons/Schedule';
import DriveEtaIcon from '@material-ui/icons/DriveEta';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import BuildIcon from '@material-ui/icons/Build';
import LaunchIcon from '@material-ui/icons/Launch';
import moment from 'moment';
import chatConversationStyles from './ChatConversation.module.css';
import InitialsAvatar from './InitialsAvatar';
import dataProvider from '../dataProvider';
import {
  daysBetween,
  convertMilitaryStringWithSecondsToAmPm,
  convertPhoneFormat,
  isEmpty,
} from '../helper';

const { Text } = Typography;

const ConversationDetails = ({
  selectedConversation,
  selectedConversation: {
    channelState: { attributes, friendlyName },
  },
  setError,
}) => {
  const [customer, setCustomer] = useState({});
  const [lead, setLead] = useState({});
  const [appointmentToDisplay, setAppointmentToDisplay] = useState(null);
  const [vehicleName, setVehicleName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getVehicleName = (customerObj, sw) => {
    const vehicle = customerObj.vehicles?.find((v) => v.id === sw.vehicle_id);
    setVehicleName(vehicle?.display_name);
  };

  const getMostRecentWaitlistedAppt = (waitlistedAppts) => {
    waitlistedAppts.sort((a, b) => {
      return Date.parse(b.created_at) - Date.parse(a.created_at);
    });
    return waitlistedAppts[0];
  };

  const getAppointmentToDisplay = (customerObj) => {
    if (isEmpty(customerObj)) {
      return;
    }
    const { sws } = customerObj;
    const activeSws = sws.filter(
      (sw) => sw.status !== 'cancelled' && sw.status !== 'suggested',
    );

    let min = 10 ** 1000;
    let sw = null;
    const waitlistedAppts = [];
    const today = new Date();

    for (let i = 0; i < activeSws.length; i++) {
      if (!activeSws[i].date) {
        waitlistedAppts.push(activeSws[i]);
      } else if (Math.abs(daysBetween(today, activeSws[i].date)) <= min) {
        min = Math.abs(daysBetween(today, activeSws[i].date));
        sw = activeSws[i];
      }
    }
    if (sw) {
      setAppointmentToDisplay(sw);
    } else if (waitlistedAppts.length > 0) {
      setAppointmentToDisplay(getMostRecentWaitlistedAppt(waitlistedAppts));
    }
  };

  // If conversation includes a customer/lead, fetch details
  useEffect(() => {
    // Prevent race condition: https://medium.com/hackernoon/avoiding-race-conditions-when-fetching-data-with-react-hooks-220d6fd0f663
    let canceled = false;

    if (attributes.customer_participant_sid) {
      setCustomer({});
      setLead({});
      setAppointmentToDisplay(null);
      setVehicleName(null);
      setIsLoading(true);
      selectedConversation
        .getParticipantBySid(attributes.customer_participant_sid)
        .then((customerMember) => {
          if (canceled) {
            return;
          }
          const {
            state: { attributes: memberAttributes },
          } = customerMember;
          if (memberAttributes?.lead_id) {
            return dataProvider
              .getOne('lead', { id: memberAttributes.lead_id })
              .then(({ data }) => {
                if (canceled) {
                  return;
                }
                if (!data) {
                  setIsLoading(false);
                  return setError('Error retrieving lead details');
                }
                setLead(data);
                setCustomer({});
                setIsLoading(false);
              });
          }
          if (memberAttributes?.customer_id) {
            return dataProvider
              .getOne('customer', { id: memberAttributes.customer_id })
              .then(({ data }) => {
                if (canceled) {
                  return;
                }
                if (!data) {
                  setIsLoading(false);
                  return setError('Error retrieving customer details');
                }
                setLead({});
                setCustomer(data);
                getAppointmentToDisplay(data);
                setIsLoading(false);
              });
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.log('error: ', error);
          setIsLoading(false);
        });
    }

    return () => (canceled = true);
  }, [selectedConversation]);

  useEffect(() => {
    getVehicleName(customer, appointmentToDisplay);
  }, [appointmentToDisplay]);

  const handleSwClick = () => {
    window.open(
      `#/scheduled-work/${appointmentToDisplay.scheduled_work_id}/show`,
    );
  };

  // const handleProfileClick = () => {
  //   window.open(`#/customer/${customer.id}/show`);
  // };

  const openSimpleShare = () => {
    window.parent.postMessage(
      `customerId=${customer.id}`,
      window.location.host.includes('staging')
        ? 'https://simple360-temp-staging-branch-simpletire.vercel.app/'
        : 'https://app.simple360.com',
    );
  };

  const renderAppointmentTime = () => {
    if (
      appointmentToDisplay.planned_arrival_time &&
      appointmentToDisplay.planned_departure_time
    ) {
      return `${convertMilitaryStringWithSecondsToAmPm(
        appointmentToDisplay.planned_arrival_time,
      )} - ${convertMilitaryStringWithSecondsToAmPm(
        appointmentToDisplay.planned_departure_time,
      )}`;
    }
    if (appointmentToDisplay.confirmed_appointment_start_time) {
      return convertMilitaryStringWithSecondsToAmPm(
        appointmentToDisplay.confirmed_appointment_start_time,
      );
    }
    return 'Time not set';
  };

  const avatar = (
    <div
      style={{
        textAlign: 'center',
        cursor: !isEmpty(customer) ? 'pointer' : 'default',
      }}
      onClick={!isEmpty(customer) ? openSimpleShare : null}
    >
      {isLoading ? (
        <Spin spinning />
      ) : (
        <InitialsAvatar
          name={friendlyName}
          size="50"
          color="rgba(0, 0, 0, 0.85)"
        />
      )}
    </div>
  );

  let customerOrLeadContactInfo = '';
  if (attributes.customer_participant_sid) {
    const primaryAddress = customer.addresses?.find((a) => a.is_primary);
    customerOrLeadContactInfo = (
      <List.Item>
        <Card bordered={false} title={avatar} style={{ width: '100%' }}>
          {!isLoading &&
            isEmpty(customer) &&
            isEmpty(lead) &&
            'Details are not available for this customer'}
          {(customer.phone || lead.phone) && (
            <div>
              <Icon type="phone" style={{ marginRight: '5px' }} />
              {convertPhoneFormat(customer.phone) ||
                convertPhoneFormat(lead.phone)}
            </div>
          )}
          {(customer.email || lead.email) && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon type="mail" style={{ marginRight: '5px' }} />
              <Text ellipsis>{customer.email || lead.email}</Text>
            </div>
          )}
          {primaryAddress && (
            <div>
              <Icon type="home" style={{ marginRight: '5px' }} />
              {primaryAddress.city}, {primaryAddress.state}
            </div>
          )}
        </Card>
      </List.Item>
    );
  }

  let customerApptCard;
  let customerApptCardHeader = 'Appointments';
  if (!isEmpty(customer)) {
    const today = new Date();
    const swClickProps = {
      style: {
        textAlign: 'center',
        cursor: 'pointer',
      },
      onClick: handleSwClick,
    };

    if (!appointmentToDisplay) {
      customerApptCardHeader = (
        <div style={{ textAlign: 'center' }}>
          <RemoveCircleOutlineOutlinedIcon style={{ fontSize: '40px' }} />
          <div>No Appointments</div>
        </div>
      );
    } else if (daysBetween(today, appointmentToDisplay.date) === 0) {
      customerApptCardHeader = (
        <div {...swClickProps}>
          <BlurCircularOutlinedIcon style={{ fontSize: '40px' }} />
          <div>Active Appointment</div>
        </div>
      );
    } else if (daysBetween(today, appointmentToDisplay.date) > 0) {
      customerApptCardHeader = (
        <div {...swClickProps}>
          <AssignmentTurnedInOutlinedIcon style={{ fontSize: '40px' }} />
          <div>Most Recent Appointment</div>
        </div>
      );
    } else {
      customerApptCardHeader = (
        <div {...swClickProps}>
          <InsertInvitationOutlinedIcon style={{ fontSize: '40px' }} />
          <div>Upcoming Appointment</div>
        </div>
      );
    }

    customerApptCard = (
      <List.Item>
        <Card
          bordered={false}
          title={customerApptCardHeader}
          style={{ width: '100%' }}
        >
          {appointmentToDisplay && (
            <>
              <div className="appointment-details">
                <div className={chatConversationStyles['appt-detail']}>
                  <LocationOnIcon className={chatConversationStyles.mr10} />
                  {appointmentToDisplay.customer_address
                    ?.multi_line_human_readable_address ||
                    appointmentToDisplay.client_location?.address
                      ?.multi_line_human_readable_address}
                </div>
                <div className={chatConversationStyles['appt-detail']}>
                  <ScheduleIcon className={chatConversationStyles.mr10} />
                  {appointmentToDisplay?.date
                    ? moment(appointmentToDisplay.date).format(
                        'ddd, MMM D, YYYY',
                      )
                    : 'Waitlisted'}
                  <br />
                  {renderAppointmentTime()}
                </div>
                {vehicleName && (
                  <div className={chatConversationStyles['appt-detail']}>
                    <DriveEtaIcon className={chatConversationStyles.mr10} />
                    {vehicleName}
                  </div>
                )}
                <div className={chatConversationStyles['appt-detail']}>
                  <BuildIcon className={chatConversationStyles.mr10} />
                  {appointmentToDisplay.service_vehicle_name}
                </div>
                <div className={chatConversationStyles['appt-detail']}>
                  <AssignmentIndIcon className={chatConversationStyles.mr10} />
                  <b>Technician:&nbsp;</b>
                  {appointmentToDisplay.technician_name || 'N/A'}
                </div>
                <div>
                  <ul>
                    {appointmentToDisplay.services.map((s) => (
                      <li key={s.id}>{s.display_name}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
          <div style={{ textAlign: 'center' }}>
            <Button
              label="View Customer Profile"
              onClick={openSimpleShare}
              className="btnToLink"
            >
              View Customer Profile <LaunchIcon style={{ fontSize: '15px' }} />
            </Button>
          </div>
        </Card>
      </List.Item>
    );
  }

  return (
    <>
      <List
        className={chatConversationStyles['chat-details']}
        bordered
        style={{ height: '100%', overflow: 'scroll' }}
      >
        {customerOrLeadContactInfo}
        {(!isEmpty(customer) || !isEmpty(lead)) && customerApptCard}
      </List>
    </>
  );
};

export default ConversationDetails;
